/**
 * ColorPicker Component
 *
 * A color picker control for selecting the QR code foreground color.
 * Displays a native color input with hex value display and validates
 * color contrast against white for QR code scannability.
 *
 * Requirements:
 * - 3.1: Display a color picker control for selecting the QR code foreground color
 * - 3.4: Default the QR code color to black (#000000)
 * - 3.5: Validate that the selected color provides sufficient contrast against white
 */

import { useCallback, useId } from 'react';
import { validateColorContrast } from '../utils/colorValidation';
import './ColorPicker.css';

/**
 * Props for the ColorPicker component
 */
export interface ColorPickerProps {
  /** Current color value in hex format (e.g., '#000000') */
  readonly color: string;
  /** Callback fired when the color changes */
  readonly onChange: (color: string) => void;
}

/**
 * ColorPicker - QR code foreground color selection
 *
 * Provides a color input with hex value display and contrast validation.
 * Shows a warning when the selected color has insufficient contrast against
 * white for reliable QR code scanning.
 *
 * @example
 * ```tsx
 * <ColorPicker
 *   color="#000000"
 *   onChange={(newColor) => setColor(newColor)}
 * />
 * ```
 */
export function ColorPicker({ color, onChange }: ColorPickerProps): JSX.Element {
  const inputId = useId();
  const hasValidContrast = validateColorContrast(color);

  const handleColorChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target.value);
    },
    [onChange]
  );

  const handleHexInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      let value = event.target.value;
      
      // Ensure the value starts with #
      if (!value.startsWith('#')) {
        value = '#' + value;
      }
      
      // Only update if it's a valid hex format (partial or complete)
      const hexPattern = /^#[0-9A-Fa-f]{0,6}$/;
      if (hexPattern.test(value)) {
        // Only call onChange for complete hex values
        if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
          onChange(value.toUpperCase());
        }
      }
    },
    [onChange]
  );

  return (
    <div className="color-picker">
      <label className="color-picker__label" htmlFor={inputId}>
        QR Code Color
      </label>
      
      <div className="color-picker__controls">
        <div className="color-picker__input-wrapper">
          <input
            id={inputId}
            type="color"
            className="color-picker__color-input"
            value={color}
            onChange={handleColorChange}
            aria-describedby={!hasValidContrast ? `${inputId}-warning` : undefined}
          />
          <div 
            className="color-picker__swatch"
            style={{ backgroundColor: color }}
            aria-hidden="true"
          />
        </div>
        
        <input
          type="text"
          className="color-picker__hex-input"
          value={color.toUpperCase()}
          onChange={handleHexInputChange}
          maxLength={7}
          placeholder="#000000"
          aria-label="Hex color value"
        />
      </div>
      
      {!hasValidContrast && (
        <div 
          id={`${inputId}-warning`}
          className="color-picker__warning"
          role="alert"
        >
          <svg 
            className="color-picker__warning-icon" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
            aria-hidden="true"
          >
            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <span>
            Low contrast: This color may affect QR code scannability. 
            Choose a darker color for better results.
          </span>
        </div>
      )}
    </div>
  );
}
